import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { profilesApi } from '../../api';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSelect from '../controls/HrnbSelect';
import { SaveIcon, DetailsIcon } from '../../icons';
import HrnbFormControl from '../controls/HrnbFormControl';
import HrnbLabel from '../controls/HrnbLabel';
import HrnbCard from '../controls/HrnbCard';
import { HrnbListGroup, HrnbListGroupItem } from '../controls/HrnbListGroup';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';

const PROFILE_TYPE_OPTIONS = [
    { value: '', label: 'Не указан' },
    { value: 'MC', label: 'MC (исполнитель)' },
    { value: 'JUDGE', label: 'Судья' },
    { value: 'ORGANIZER', label: 'Организатор' },
    { value: 'SPECTATOR', label: 'Зритель' },
];

const PLATFORM_OPTIONS = [
    { value: '', label: 'Платформа' },
    { value: 'VK', label: 'VK' },
    { value: 'TELEGRAM', label: 'Telegram' },
    { value: 'YOUTUBE', label: 'YouTube' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'OTHER', label: 'Другое' },
];

// Приводим socialLinks к единому формату [{ platform, url }]
function normalizeSocialLinks(links) {
    if (!links || links.length === 0) return [{ platform: '', url: '' }];
    if (typeof links[0] === 'string') {
        return links.map(link => {
            const parts = link.split(':');
            return parts.length > 1
                ? { platform: parts[0], url: parts.slice(1).join(':') }
                : { platform: 'OTHER', url: link };
        });
    }
    return links;
}

export default function ProfileCard({ profile, onDirtyChange }) {
    const navigate = useNavigate();

    // Снимок исходных данных для сравнения dirty-состояния
    const initialSnapshotRef = useRef({
        displayName: profile?.displayName || '',
        bio: profile?.bio || '',
        profileType: profile?.profileType || '',
        city: profile?.city || '',
        country: profile?.country || '',
        socialLinks: normalizeSocialLinks(profile?.socialLinks),
    });

    const [editedProfile, setEditedProfile] = useState({
        displayName: profile?.displayName || '',
        bio: profile?.bio || '',
        profileType: profile?.profileType || '',
        city: profile?.city || '',
        country: profile?.country || '',
        socialLinks: normalizeSocialLinks(profile?.socialLinks)
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    const isDirty = useMemo(() => {
        const snap = initialSnapshotRef.current;
        const socialChanged = JSON.stringify(editedProfile.socialLinks) !== JSON.stringify(snap.socialLinks);
        return editedProfile.displayName !== snap.displayName
            || editedProfile.bio !== snap.bio
            || editedProfile.profileType !== snap.profileType
            || editedProfile.city !== snap.city
            || editedProfile.country !== snap.country
            || socialChanged;
    }, [editedProfile]);

    useUnsavedChanges(isDirty);

    // Уведомляем родителя об изменении dirty-состояния
    useEffect(() => {
        if (onDirtyChange) onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    // Автоисчезновение success-алерта через 3 сек
    const saveSuccessTimerRef = useRef(null);
    useEffect(() => {
        if (saveSuccess) {
            clearTimeout(saveSuccessTimerRef.current);
            saveSuccessTimerRef.current = setTimeout(() => setSaveSuccess(''), 3000);
        }
        return () => clearTimeout(saveSuccessTimerRef.current);
    }, [saveSuccess]);

    const validate = () => {
        const errors = {};
        if (!editedProfile.displayName.trim()) {
            errors.displayName = 'Имя профиля обязательно';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveChanges = async () => {
        if (!validate()) return;
        setSaving(true);
        setSaveError('');
        setSaveSuccess('');
        const filteredLinks = editedProfile.socialLinks
            .filter(link => link.platform && link.url)
            .map(link => ({ platform: link.platform, url: link.url }));
        const requestBody = {
            displayName: editedProfile.displayName,
            bio: editedProfile.bio,
            profileType: editedProfile.profileType || '',
            city: editedProfile.city || null,
            country: editedProfile.country || null,
            socialLinks: filteredLinks.length > 0 ? filteredLinks : undefined,
        };
        try {
            const updated = await profilesApi.update(profile.id, requestBody);
            const savedData = {
                displayName: updated.displayName || requestBody.displayName,
                bio: updated.bio || requestBody.bio,
                profileType: updated.profileType || requestBody.profileType || '',
                city: updated.city || requestBody.city || '',
                country: updated.country || requestBody.country || '',
            };
            const savedSocialLinks = normalizeSocialLinks(updated.socialLinks || requestBody.socialLinks);
            setEditedProfile({
                ...savedData,
                socialLinks: savedSocialLinks
            });
            // Обновляем снимок — после сохранения это baseline
            initialSnapshotRef.current = { ...savedData, socialLinks: savedSocialLinks };
            setSaveSuccess('Изменения сохранены');
        } catch (err) {
            console.error(err);
            setSaveError(err?.response?.data?.message || err?.message || 'Ошибка при сохранении профиля');
        } finally {
            setSaving(false);
        }
    };

    const handleSocialLinkChange = (index, field, value) => {
        setEditedProfile(prev => {
            const links = [...prev.socialLinks];
            links[index] = { ...links[index], [field]: value };
            return { ...prev, socialLinks: links };
        });
    };

    const addSocialLink = () => {
        setEditedProfile(prev => ({
            ...prev,
            socialLinks: [...prev.socialLinks, { platform: '', url: '' }]
        }));
    };

    const removeSocialLink = (index) => {
        setEditedProfile(prev => ({
            ...prev,
            socialLinks: prev.socialLinks.filter((_, i) => i !== index)
        }));
    };

    return (
        <HrnbCard title="Публичный профиль">
            {saveSuccess && <HrnbAlert variant="success" onClose={() => setSaveSuccess('')}>{saveSuccess}</HrnbAlert>}
            {saveError && <HrnbAlert variant="danger" onClose={() => setSaveError('')}>{saveError}</HrnbAlert>}
            {validationErrors.displayName && <HrnbAlert variant="danger" onClose={() => setValidationErrors({})}>{validationErrors.displayName}</HrnbAlert>}
            <HrnbListGroup variant="flush">
                <HrnbListGroupItem>
                    <HrnbLabel text="Имя профиля:" required />
                    <HrnbFormControl
                        type="text"
                        value={editedProfile.displayName}
                        onChange={(e) => {
                            setEditedProfile(prev => ({ ...prev, displayName: e.target.value }));
                            setValidationErrors(prev => ({ ...prev, displayName: '' }));
                        }}
                    />
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Тип профиля:" />
                    <HrnbSelect
                        value={editedProfile.profileType}
                        onChange={(val) => setEditedProfile(prev => ({ ...prev, profileType: val }))}
                        options={PROFILE_TYPE_OPTIONS}
                    />
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Биография:" required />
                    <HrnbFormControl
                        as="textarea"
                        value={editedProfile.bio}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, bio: e.target.value }))}
                    />
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Город:" />
                    <HrnbFormControl
                        type="text"
                        value={editedProfile.city}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, city: e.target.value }))}
                    />
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Страна:" />
                    <HrnbFormControl
                        type="text"
                        value={editedProfile.country}
                        onChange={(e) => setEditedProfile(prev => ({ ...prev, country: e.target.value }))}
                    />
                </HrnbListGroupItem>

                <HrnbListGroupItem>
                    <HrnbLabel text="Социальные сети:" />
                    {editedProfile.socialLinks.map((link, index) => (
                        <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <HrnbSelect
                                value={link.platform}
                                onChange={(value) => handleSocialLinkChange(index, 'platform', value)}
                                options={PLATFORM_OPTIONS}
                                style={{ flex: 1, minWidth: 120 }}
                            />
                            <HrnbFormControl
                                type="text"
                                placeholder="https://..."
                                value={link.url}
                                onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                                style={{ flex: 2 }}
                                maxLength={500}
                            />
                            {editedProfile.socialLinks.length > 1 && (
                                <HrnbButton
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeSocialLink(index)}
                                >
                                    ✕
                                </HrnbButton>
                            )}
                        </div>
                    ))}
                    <HrnbButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={addSocialLink}
                    >
                        + Добавить ссылку
                    </HrnbButton>
                </HrnbListGroupItem>
            </HrnbListGroup>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <HrnbButton onClick={handleSaveChanges} icon={<SaveIcon />} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить изменения'}
                </HrnbButton>
                <HrnbButton
                    variant="outline-secondary"
                    icon={<DetailsIcon />}
                    onClick={() => navigate(`/profile/${profile.id}`)}
                >
                    Просмотр профиля
                </HrnbButton>
            </div>
        </HrnbCard>
    );
}

ProfileCard.propTypes = {
    profile: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        displayName: PropTypes.string,
        bio: PropTypes.string,
        profileType: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
        socialLinks: PropTypes.array
    }).isRequired,
    onDirtyChange: PropTypes.func,
};
